import CustomScrollbar from 'app/components/CustomScrollbar';
import { SecondaryIndicationSelector } from 'app/components/IndicationSelector';
import { useAnalytics } from 'app/context/Analytics';
import { ChatModuleType, type FAQPage } from 'app/copied/models/ChatModules';
import useFetchWebSearchResults from 'app/hooks/fetch/useFetchWebSearchResults';
import useFeatureFlags from 'app/hooks/useFeatureFlags';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import useModule from 'app/hooks/useModule';
import useUiConfig from 'app/hooks/useUiConfig';
import SearchFeedback from 'app/components/Feedback/SearchFeedback';

import Header, { BackButtonAction } from '../../../components/Header';
import Spinner from '../../../components/Spinner';
import { PosthogEventName } from '../../../copied/constants/PosthogEventName';
import { waitForIDs } from '../../../copied/getIds';
import useFetchSearchResults from '../../../hooks/fetch/useFetchSearchResults';
import useFooterLinks from '../../../hooks/useFooterLinks';
import TailorLooking from '../components/TailorLooking';
import SearchInput from '../components/TailorSearchInput';
import TailorSearchResults from '../components/TailorSearchResults';
import useHandleSearchResultClick from '../hooks/useHandleSearchResultClick';
import { useSearch } from '../hooks/useSearch';
import IntegratedSearchInput from '../components/IntegratedSearchInput';

export default function TailorSearch() {
  const [results, setResults] = useState<FAQPage[]>([]);
  const { search, setSearch } = useSearch();
  const [deferredSearch, setDeferredSearch] = useState(search);
  const [searchTraceId, setSearchTraceId] = useState<string>(uuidv4());
  const { t } = useTranslation();
  const flags = useFeatureFlags();
  const { captureEvent } = useAnalytics();
  const integratedSearchModule = useModule(ChatModuleType.IntegratedSearch);
  const timeout = integratedSearchModule ? 0 : 500;
  const uiConfig = useUiConfig();
  const handleSearchClick = (searchValue: string) => {
    if (searchValue !== deferredSearch) {
      setDeferredSearch(searchValue);
      setSearchTraceId(uuidv4());
    }
  };

  useEffect(
    function handleSearchDebounceAndUpdate() {
      if (integratedSearchModule) {
        return;
      }

      const timeoutId = setTimeout(() => {
        if (search !== deferredSearch) {
          setDeferredSearch(search);
          setSearchTraceId(uuidv4());
        }
      }, timeout);

      return () => {
        clearTimeout(timeoutId);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search, integratedSearchModule]
  );

  const { data, isLoading: isSearchLoading } = useFetchSearchResults({
    search: deferredSearch,
    suggested: !deferredSearch,
    traceId: searchTraceId, // Add trace ID to search request
  });
  const { data: webSearchData, isLoading: isWebSearchLoading } = useFetchWebSearchResults({
    query: deferredSearch,
    enabled: flags.webSearchInTailor,
  });

  const isLoading = isSearchLoading || isWebSearchLoading;
  useEffect(() => {
    if (!flags.webSearchInTailor) {
      setResults(data?.results || []);
      return;
    }

    if (data && webSearchData) {
      const combinedResults = [
        ...data.results.slice(0, 2),
        ...webSearchData.results.slice(0, 2),
        ...data.results.slice(2),
        ...webSearchData.results.slice(2),
      ];
      setResults(combinedResults);
    } else if (data) {
      setResults(data.results);
    } else if (webSearchData) {
      setResults(webSearchData.results);
    }
  }, [data, webSearchData, flags.webSearchInTailor]);

  const { secondaryFooterLink } = useFooterLinks();

  const handleResultSelect = useHandleSearchResultClick();

  useEffect(() => {
    captureEvent(PosthogEventName.LoadSearchScreen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (deferredSearch) {
      waitForIDs().then(({ conditionId }) => {
        captureEvent(PosthogEventName.QuerySubmitted, {
          conditionUUID: conditionId,
          isFlow: false,
          queryTyped: deferredSearch,
          searchSource: 'Search result',
          requestId: searchTraceId,
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTraceId]);

  useEffect(() => {
    if (data?.results && !isLoading) {
      captureEvent(PosthogEventName.SearchQueryLoad, {
        results: data.results.map((value) => value.title),
        searchSnippet: data.results.map((value) => !!value.highlight_tag_text),
        result_scores: data.results.map((value) => value.result_rank),
        queryTyped: deferredSearch,
        requestId: searchTraceId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.results, deferredSearch, isLoading]);

  return (
    <CustomScrollbar
      id="ostro-tailor-search-container"
      className="flex flex-col"
      rememberScrollPosition
    >
      <div className="grow px-7 py-5">
        <Header backButtonAction={BackButtonAction.HOME} />

        {integratedSearchModule ? (
          <IntegratedSearchInput
            placeholder={integratedSearchModule.search_suggestions?.[0]?.text?.replace(/<[^>]*>/g, '')}
            value={search}
            onChange={setSearch}
            onClickSearch={handleSearchClick}
            disableWhenEmpty={true}
            className="mt-[15px] mb-[32px]"
            displayClearButton={!!deferredSearch && search === deferredSearch && !isLoading}
            disabled={isLoading}
            ariaLabel={t('search_placeholder')}
          />
        ) : (
          <SearchInput
            search={search}
            onChange={setSearch}
            className="mt-[15px] mb-[34px]"
            ariaLabel={t('search_placeholder')}
          />
        )}

        {isLoading ? (
          <div className="grow flex justify-center mt-24">
            <Spinner />
          </div>
        ) : null}

        {!deferredSearch && !isLoading ? (
          <h3 className="mb-3 text-typography-sectionHeader text-sectionHeader">{t('most_requested_resources')}</h3>
        ) : null}

        {deferredSearch && !isLoading && results.length > 0 && integratedSearchModule && (
          <h3 className="mb-3 text-typography-sectionHeader text-sectionHeader capitalize">
            {t('search_relevant_resources')}
          </h3>
        )}

        {isLoading ? null : (
          <div className="grow">
            <TailorSearchResults
              showNoResults={true}
              highlightTerms={deferredSearch}
              results={results}
              onSelectResult={(result, index) =>
                handleResultSelect({
                  result,
                  resultIndex: index + 1,
                  isSuggested: !search,
                  linkLocation: 'topicSearchResult',
                })
              }
            />
          </div>
        )}
      </div>
      {uiConfig?.feedback?.faqFeedbackEnabled && deferredSearch && !isLoading && (
        <SearchFeedback searchQuery={deferredSearch} />
      )}
      <SecondaryIndicationSelector className="px-7 pb-5 grow" />
      <TailorLooking footerLink={secondaryFooterLink} />
    </CustomScrollbar>
  );
}
